import React, { useEffect, useState } from 'react';
import { isIOS, osVersion } from 'react-device-detect';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import MultiStepActivationContainer from './Components/MultiStepActivationContainer';
import { useTranslation, Trans } from 'react-i18next';
import { themeColor } from '../Theming';
import images from '../Theming/images';
import { FirstA, SecondA } from './Components/InstructionStep';
import { Path } from '../Routing';
import TapHereAnimated from './Components/TapHereAnimated';
import env from '../env';

export const MultiStepHeaderText = styled.h3<MultiStepProps>`
  color: ${(props) => themeColor(props.sliide ? 'black' : 'smokeBlack')};
  text-align: center;
  font-size: 22px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
`;

const SubTitleTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SubTitleText = styled.div`
  color: ${themeColor('darkestGrey')};
  text-align: center;
  font-size: 15px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  width: 310px;
`;

const LinkText = styled.a<MultiStepProps>`
  color: ${(props) => themeColor(props.sliide ? 'verizonRed' : 'blue')};
  text-decoration: none;
`;

export const HyperisePlaceholderContainer = styled.div`
  width: 100%;
  height: fit-content;
`;

const BodyContentContainer = styled.div`
  width: 320px;
  min-height: 106px;
  margin-top: 33px;
  margin-left: 50px;
`;

const IconContentContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  padding-bottom: 23px;
`;

const IconContentDescription = styled.div`
  width: 253px;
  color: ${themeColor('darkestGrey')};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 21px;
  margin-left: 15px;
`;

const StyledImage = styled.img`
  margin-top: 5px;
`;

const TextTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  line-height: 15px;
  align-items: center;
`;

const StrongText = styled.strong`
  padding-left: 2px;
  padding-right: 2px;
`;

export interface MultiStepProps {
  sliide?: boolean;
}

export const addSliideText = (sliide: boolean | undefined) => {
  return sliide ? 'Sliide' : '';
};

const PartnerPluginActivateMultiStep: React.FC<MultiStepProps> = ({
  sliide,
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const supportsiOS18 = isIOS && parseInt(osVersion) >= 18;

  useEffect(() => {
    const initialHeight = window.innerHeight;

    const interval = setInterval(() => {
      const currentHeight = window.innerHeight;

      if (currentHeight < initialHeight) {
        history.push({
          pathname: Path.PluginActivationReviewPermissions,
          search: location.search,
        });
      }
    }, 1500);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const messageHandler = (message: any) => {
      if (message) {
        switch (message.data?.type) {
          case 'PluginEnabled': {
            if (message.data?.givenPermission) {
              history.push({
                pathname: Path.PluginActivationStartShopping,
                search: location.search,
              });
            } else {
              history.push({
                pathname: Path.PluginActivationAllowPermissions,
                search: location.search,
              });
            }
            break;
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      clearInterval(interval);
      window.removeEventListener('message', messageHandler);
    };
  }, [history]);

  return (
    <MultiStepActivationContainer
      headerTextWidth={240}
      headerTextColor="#323A68"
      sliideSubtitle="PartnerpluginsdkActivate.multiStep.neverMissADeal"
      headerText={
        'PartnerpluginsdkActivate.multiStep.followTheseEasySteps' +
        addSliideText(sliide)
      }
      sliide={sliide}
    >
      <TapHereAnimated sliide={sliide} />
      <MultiStepHeaderText sliide={sliide}>
        {t(
          'PartnerpluginsdkActivate.multiStep.enableKindred' +
            addSliideText(sliide)
        )}
      </MultiStepHeaderText>
      <SubTitleTextContainer>
        <SubTitleText>
          <Trans
            t={t}
            i18nKey="PartnerpluginsdkActivate.multiStep.neverSellOrViewData"
          >
            <LinkText
              href={env.privacy_policy_url}
              target="_blank"
              sliide={sliide}
            >
              AbsoluteText
            </LinkText>
          </Trans>
        </SubTitleText>
      </SubTitleTextContainer>
      <BodyContentContainer>
        <IconContentContainer>
          <StyledImage
            src={images.puzzle_icon?.default}
            width={38}
            height={31}
            alt="puzzle"
          />
          <IconContentDescription>
            {!supportsiOS18 ? (
              <Trans
                i18nKey="PartnerpluginsdkActivate.multiStep.tapTheAa"
                t={t}
              >
                <FirstA>A</FirstA>
                <SecondA>A</SecondA>
              </Trans>
            ) : (
              <TextTitleContainer>
                <span>
                  {t('PartnerpluginsdkActivate.multiStep.tapTheAaPart1')}
                </span>
                <StyledImage
                  src={images.partnerpluginsdk_ios_18_plugin_icon?.default}
                  width={21}
                  height={25}
                  alt="ios_18_plugin"
                />
                <Trans
                  i18nKey="PartnerpluginsdkActivate.multiStep.tapTheAaPart2"
                  t={t}
                >
                  <StrongText>BoldText</StrongText>
                </Trans>
              </TextTitleContainer>
            )}
          </IconContentDescription>
        </IconContentContainer>
        <IconContentContainer>
          <StyledImage
            src={images.ios_checked_icon?.default}
            width={42}
            height={23}
            alt="puzzle"
          />
          <IconContentDescription>
            <Trans
              i18nKey={`PartnerpluginsdkActivate.multiStep.toggleOn${addSliideText(
                sliide
              )}`}
              t={t}
            >
              <FirstA>A</FirstA>
            </Trans>
          </IconContentDescription>
        </IconContentContainer>
      </BodyContentContainer>
    </MultiStepActivationContainer>
  );
};

export default PartnerPluginActivateMultiStep;
